import type { PostHog } from 'posthog-react-native'

export type FlowContext = 'route' | 'bottom_sheet'

export type FlowProps = Record<string, string | number | null>

export const FLOW_EVENTS = {
  start: 'flow_start',
  view: 'flow_phase_view',
  complete: 'flow_complete',
  exit: 'flow_exit',
  reset: 'flow_reset',
} as const

export function createFlowRunId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

export function nowMs(): number {
  return Date.now()
}

export interface FlowTrackerConfig {
  flowId: string
  flowTitle?: string
  context: FlowContext
  route?: string
  posthog: PostHog | null
}

export class FlowTracker {
  private config: FlowTrackerConfig
  private flowRunId: string
  private startTime: number
  private lastPhaseId: string | null = null
  private visitedPhases: Set<string> = new Set()

  constructor(config: FlowTrackerConfig) {
    this.config = config
    this.flowRunId = createFlowRunId()
    this.startTime = nowMs()
  }

  recordStart(phaseId: string): void {
    if (!this.config.posthog) {
      return
    }

    const props = this.buildProps(phaseId)
    this.config.posthog.capture(FLOW_EVENTS.start, props)
    this.visitedPhases.add(phaseId)
    this.lastPhaseId = phaseId
  }

  recordPhaseView(phaseId: string): void {
    if (!this.config.posthog) {
      return
    }

    const props = this.buildProps(phaseId)
    const phaseViewProps = {
      ...props,
      previous_phase_id: this.lastPhaseId ?? null,
    }

    this.config.posthog.capture(FLOW_EVENTS.view, phaseViewProps)
    this.visitedPhases.add(phaseId)
    this.lastPhaseId = phaseId
  }

  recordComplete(success: boolean): void {
    if (!this.config.posthog) {
      return
    }

    const durationMs = nowMs() - this.startTime
    const props = this.buildProps(this.lastPhaseId ?? '')
    const completeProps = {
      ...props,
      success: success ? 'true' : 'false',
      duration_ms: durationMs,
      visited_phases_count: this.visitedPhases.size,
    }

    this.config.posthog.capture(FLOW_EVENTS.complete, completeProps)
  }

  recordExit(reason: 'close' | 'back_from_first'): void {
    if (!this.config.posthog) {
      return
    }

    const props = this.buildProps(this.lastPhaseId ?? '')
    const exitProps = {
      ...props,
      reason,
    }

    this.config.posthog.capture(FLOW_EVENTS.exit, exitProps)
  }

  recordReset(newPhaseId: string): void {
    if (!this.config.posthog) {
      return
    }

    const props = this.buildProps(this.lastPhaseId ?? '')
    const resetProps = {
      ...props,
      new_phase_id: newPhaseId,
    }

    this.config.posthog.capture(FLOW_EVENTS.reset, resetProps)
    this.reset(newPhaseId)
  }

  reset(newPhaseId: string): void {
    this.flowRunId = createFlowRunId()
    this.startTime = nowMs()
    this.lastPhaseId = null
    this.visitedPhases = new Set([newPhaseId])
  }

  private buildProps(phaseId: string): FlowProps {
    return {
      flow_id: this.config.flowId,
      flow_title: this.config.flowTitle ?? null,
      flow_run_id: this.flowRunId,
      context: this.config.context,
      route: this.config.route ?? null,
      current_phase_id: phaseId,
    }
  }
}
